import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Radio, MonitorPlay, ShieldCheck, Search, EyeOff, Eye } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Reveal, Overline } from "../components/motion";
import { Discussion } from "../components/Discussion";
import { FollowButton, StatusPill, AvailabilityRow } from "../components/tvui";
import { useTranslate } from "../context/TranslateContext";
import { getSeries, episodesForSeries, getChannel, getPlatform, castNames } from "../mock/tv";

const hex = (h, a) => { const n = h.replace("#", ""); return `rgba(${parseInt(n.slice(0,2),16)},${parseInt(n.slice(2,4),16)},${parseInt(n.slice(4,6),16)},${a})`; };
const PAGE = 12;

export default function SeriesDetail() {
  const { slug } = useParams();
  const { t } = useTranslate();
  const s = getSeries(slug);
  const [q, setQ] = useState("");
  const [season, setSeason] = useState("All");
  const [page, setPage] = useState(1);
  const [showSpoilers, setShowSpoilers] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); setPage(1); }, [slug]);

  const eps = episodesForSeries(slug);
  const filtered = useMemo(() => eps.filter((e) =>
    (season === "All" || e.season === Number(season)) &&
    (!q || `${e.title} ${e.summary}`.toLowerCase().includes(q.toLowerCase()))
  ), [eps, q, season]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageEps = filtered.slice((page - 1) * PAGE, page * PAGE);

  if (!s) return (<div><InnerNav /><div className="p-20 text-center font-body text-ash">{t("Series not found.")}</div></div>);
  const accent = s.accent;
  const ch = s.channel && getChannel(s.channel);
  const pf = s.platform && getPlatform(s.platform);
  const listenText = `${s.title}. ${s.synopsis}`;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${hex(accent, 0.22)} 0%, #FBF6EC 62%)` }}>
      <InnerNav />
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 py-12 md:px-12 md:py-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="overflow-hidden rounded-2xl border shadow-2xl" style={{ borderColor: hex(accent, 0.5) }}>
            <img src={s.poster} alt={s.title} data-testid="series-poster" className="aspect-[2/3] w-full object-cover" />
          </div>
        </Reveal>
        <div className="lg:col-span-8">
          <div className="flex flex-wrap items-center gap-3">
            <Overline style={{ color: accent }}>{s.language} · {t(s.format)}</Overline>
            <StatusPill status={s.status} testId="series-status" />
          </div>
          <h1 data-testid="series-title" className="mt-3 font-display text-5xl font-semibold leading-none tracking-tight text-cream md:text-7xl">{s.title}</h1>
          {s.altTitles?.length > 0 && <div className="mt-2 font-body text-ash">{s.altTitles.join(" · ")}</div>}

          <div className="mt-4 flex flex-wrap items-center gap-4 font-body text-sm text-ash" data-testid="series-meta">
            <span className="rounded-full border border-edge px-3 py-1">{t(s.category)}</span>
            <span className="rounded-full border border-edge px-3 py-1">{t("Release")}: {t(s.releaseMedium)}</span>
            <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" style={{ color: accent }} />{s.start} — {s.end || t("Present")}</span>
            <span>{s.hasSeasons ? `${s.seasons} ${t("seasons")}` : t("No seasons · continuous")}</span>
            {s.episodeCount ? <span>{s.episodeCount}+ {t("episodes")}</span> : null}
          </div>
          {s.original && <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 font-body text-xs text-gold" data-testid="series-original"><ShieldCheck className="h-3.5 w-3.5" /> {t("Original")} · {getPlatform(s.original.platform)?.name} ({s.original.country})</div>}

          <p data-testid="series-synopsis" className="mt-5 max-w-2xl font-body text-lg leading-relaxed text-ash">{t(s.synopsis)}</p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <FollowButton id={s.slug} accent={accent} />
            
          </div>

          <div className="mt-5 flex flex-wrap gap-3 font-body text-sm">
            {ch && <Link to={`/channels/${ch.slug}`} className="inline-flex items-center gap-2 rounded-xl border border-edge bg-surface/50 px-4 py-2 text-cream hover:border-gold"><Radio className="h-4 w-4" style={{ color: accent }} />{ch.name}</Link>}
            {pf && <Link to={`/platforms/${pf.slug}`} className="inline-flex items-center gap-2 rounded-xl border border-edge bg-surface/50 px-4 py-2 text-cream hover:border-gold"><MonitorPlay className="h-4 w-4" style={{ color: accent }} />{pf.name}</Link>}
            <span className="inline-flex items-center gap-2 rounded-xl border border-edge bg-surface/50 px-4 py-2 text-ash"><ShieldCheck className="h-4 w-4" />{t("Last verified")}: {s.lastVerified}</span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] space-y-14 px-6 pb-28 md:px-12">
        {/* Cast */}
        <Reveal>
          <h2 className="mb-6 font-display text-3xl font-semibold text-cream">{t("Cast")}</h2>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-4 lg:grid-cols-6">
            {castNames(s).map((c) => c.person && (
              <Link key={c.p} to={`/person/${c.p}`} data-testid={`series-cast-${c.p}`} className="group text-center">
                <div className="mx-auto aspect-square w-full overflow-hidden rounded-full border border-edge"><img src={c.person.image} alt={c.person.name} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0" /></div>
                <div className="mt-2 font-body text-sm text-cream group-hover:text-gold">{c.person.name}</div>
                <div className="font-body text-xs text-ash">{t("as")} {c.as}</div>
              </Link>
            ))}
          </div>
        </Reveal>

        {/* Availability */}
        <Reveal>
          <h2 className="mb-6 font-display text-3xl font-semibold text-cream">{t("Where to Watch")}</h2>
          <div className="space-y-3">
            {s.availability.map((a, i) => <AvailabilityRow key={i} a={a} testId={`series-avail-${i}`} />)}
          </div>
          <p className="mt-3 font-body text-xs text-ash">{t("Availability at series level does not imply every episode is available. No licensed feed is connected.")}</p>
        </Reveal>

        {/* Episode guide */}
        <Reveal>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-display text-3xl font-semibold text-cream">{t("Episode Guide")}</h2>
            <div className="flex flex-wrap items-center gap-3">
              {s.hasSeasons && (
                <select value={season} onChange={(e) => { setSeason(e.target.value); setPage(1); }} data-testid="episode-season"
                  className="rounded-full border border-edge bg-surface px-4 py-2 font-body text-sm text-cream focus:outline-none">
                  <option value="All" className="bg-surface">{t("All seasons")}</option>
                  {Array.from({ length: s.seasons }, (_, i) => <option key={i} value={i + 1} className="bg-surface">{t("Season")} {i + 1}</option>)}
                </select>
              )}
              <div className="flex items-center gap-2 rounded-full border border-edge bg-surface px-4 py-2">
                <Search className="h-4 w-4 text-ash" />
                <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="episode-search" placeholder={t("Search episodes")} className="w-32 bg-transparent font-body text-sm text-cream placeholder:text-ash focus:outline-none" />
              </div>
              <button onClick={() => setShowSpoilers((v) => !v)} data-testid="spoiler-toggle" className="inline-flex items-center gap-2 rounded-full border border-edge px-3 py-2 font-body text-xs text-ash hover:text-gold">
                {showSpoilers ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {t(showSpoilers ? "Spoilers on" : "Spoilers hidden")}
              </button>
            </div>
          </div>

          {eps.length === 0 ? (
            <p className="rounded-2xl border border-edge bg-surface/40 p-8 text-center font-body text-ash" data-testid="episodes-empty">{t("Episode directory not yet available for this programme.")}</p>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-edge bg-surface/40" data-testid="episode-list">
                {pageEps.map((e, i) => (
                  <div key={`${e.season || 0}-${e.num}`} className="flex items-start gap-4 border-b border-edge px-5 py-4 last:border-0">
                    <span className="mt-0.5 w-14 shrink-0 font-display text-lg text-cream">{e.season ? `S${e.season}·${e.num}` : e.num}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-body text-cream">{e.title}</div>
                      <div className="font-body text-sm text-ash">
                        {e.spoiler && !showSpoilers ? <span className="italic">{t("Recap hidden — enable spoilers to view.")}</span> : t(e.summary)}
                      </div>
                    </div>
                    <span className="shrink-0 font-body text-xs text-ash">{e.air}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between font-body text-sm text-ash">
                <span>{filtered.length} {t("episodes")}</span>
                <div className="flex items-center gap-2" data-testid="episode-pagination">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full border border-edge px-3 py-1.5 disabled:opacity-40 hover:border-gold">‹</button>
                  <span>{page} / {pages}</span>
                  <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages} className="rounded-full border border-edge px-3 py-1.5 disabled:opacity-40 hover:border-gold">›</button>
                </div>
              </div>
            </>
          )}
        </Reveal>
      </div>
      <div className="border-t border-edge bg-paper">
        <Discussion entityType="series" entityId={slug} title="Discussion" />
      </div>
    </div>
  );
}
